<script setup lang="ts">
import { computed } from 'vue';
import {
  NxButton,
  type NxButtonPressedTone,
  type NxButtonSize,
  type NxButtonVariant,
} from './../Button';
import {
  countBadgeName,
  hasCountBadge,
  type NxCountBadgeTone,
  type NxCountBadgeValue,
} from '../lib/countBadge';
import NxCountBadge from '../lib/NxCountBadge.vue';

const props = withDefaults(
  defineProps<{
    danger?: boolean;
    /** Fully round instead of the control radius. */
    round?: boolean;
    disabled?: boolean;
    title?: string;
    ariaLabel?: string;
    height?: NxButtonSize;
    variant?: NxButtonVariant;
    /** Opt-in busy state: a spinner replaces the icon, `aria-busy` is set, clicks are ignored. */
    loading?: boolean;
    /**
     * Opt-in toggle state: sets `aria-pressed` and a visible pressed look (brand
     * tint) — NxButton's `pressed`. Unset leaves the button a plain one (no
     * `aria-pressed`).
     */
    pressed?: boolean;
    /** Tone of the pressed look, NxButton's `pressedTone` (`brand` default, `warning`). */
    pressedTone?: NxButtonPressedTone;
    /**
     * Opt-in count on the button's upper corner, the look of an `NxIconRail`
     * item's badge. `0`, `''`, `null` and `undefined` show nothing (the string
     * `'0'` shows a zero), a number above 99 reads „99+“. The count joins the
     * accessible name — „Hinweise (4)“, or „Hinweise, 4 offen“ with `badgeLabel`.
     */
    badge?: NxCountBadgeValue;
    /** Colour of the badge: `brand` (default), `neutral`, `warning` or `danger`. */
    badgeTone?: NxCountBadgeTone;
    /** Spoken meaning of the badge („4 offen“); replaces the bare count in the name. */
    badgeLabel?: string;
  }>(),
  {
    danger: false,
    disabled: false,
    title: undefined,
    ariaLabel: undefined,
    height: undefined,
    variant: 'ghost',
    loading: false,
    pressed: undefined,
    pressedTone: 'brand',
    badge: undefined,
    badgeTone: 'brand',
    badgeLabel: undefined,
  }
);

const accessibleName = computed(() => {
  const label = props.ariaLabel ?? props.title;
  return label === undefined ? undefined : countBadgeName(label, props.badge, props.badgeLabel);
});
</script>

<template>
  <NxButton
    width="icon-only"
    :variant="variant"
    :height="height"
    :class="{ danger, 'is-round': round }"
    :disabled="disabled"
    :loading="loading"
    :title="title"
    :aria-label="accessibleName"
    :pressed="pressed"
    :pressed-tone="pressedTone"
  >
    <slot />
    <NxCountBadge v-if="hasCountBadge(badge)" :badge="badge" :tone="badgeTone" />
  </NxButton>
</template>

<style scoped>
.is-round.is-round {
  border-radius: var(--nxt-radius-full);
}
/* NxButton centres every child of an icon-only button (top/left 50% and a
   translate). The count badge sits on the button's corner instead; this has the
   specificity to beat that rule without weakening it for other children. */
button.width-icon-only > .nx-count-badge {
  inset-block-start: var(--count-badge-top);
  inset-inline-start: auto;
  transform: none;
}

button.danger {
  &:hover:not(:disabled) {
    color: var(--nxt-danger-subtle-fg);
    background-color: var(--nxt-danger-subtle-bg);
  }
  &:active:not(:disabled) {
    color: var(--nxt-danger-subtle-fg);
    background-color: var(--nxt-danger-subtle-bg-hover);
  }
}
</style>
